import {
  type MarketLedgerStatus,
  type MarketSettings,
  type ProductType,
  type ProductUnit,
  saveMarketSettingsSchema,
  type StockCountStatus,
  type StockTransfer,
  type StockTransferStatus,
  type StockWriteOffStatus,
  type ExpiryStateValue,
  type WriteOffLossKey,
  type WriteOffReason,
  isStockTrackedProductType,
} from "@station/contracts";
import { Decimal, Money } from "@station/core";
import type { Tone } from "@station/ui";
import { z } from "zod";
import { apiDecimalToTrText, INVALID_DECIMAL, trTextToApiDecimal, trTextToInt } from "../pos/pos-values.js";

/**
 * Market ekranlarının SAF dönüşümleri (Prompt 16 — web). Bu dosyanın iki kuralı:
 *  1. **Ondalıklar ekranda TÜRKİYE biçiminde girilir** ("12,5") ve API'ye DİZGE olarak nokta ile gider ("12.5");
 *     `number` para / miktar için hiçbir aşamada kullanılmaz (AGENTS.md §3.2).
 *  2. **Görünmeyen alan gövdeye ÇIKMAZ:** boş bırakılan isteğe bağlı alan `null` olur, ekranda olmayan alan
 *     temizlenir; aksi hâlde sözleşme hatası GİZLİ bir alana düşer ve form sessizce gönderilmez.
 */

export { apiDecimalToTrText, INVALID_DECIMAL, trTextToApiDecimal };

// ── Miktar gösterimi ─────────────────────────────────────────────────────────
export const STOCK_UNIT_SUFFIX: Record<ProductUnit, string> = {
  LITRE: "L",
  KG: "kg",
  PIECE: "adet",
  KWH: "kWh",
  MINUTE: "dk",
};

/**
 * Stok miktarı: `numeric(18,3)` dizgesi, Türkiye biçiminde ve birimiyle. Adet / dakika tam sayıysa ondalıksız
 * yazılır ("20 adet"); kesirli miktar (tartılı ürün, düzeltme) ASLA yuvarlanmaz, gereken basamakla yazılır.
 */
export function formatStockQuantity(value: string | null | undefined, unit?: ProductUnit | null): string {
  if (value === null || value === undefined || value === "") return "—";
  const decimal = Decimal.from(value);
  const scale = (decimal.toString().split(".")[1] ?? "").replace(/0+$/, "").length;
  const wholeUnits = unit === "PIECE" || unit === "MINUTE";
  const text = decimal.formatTr(wholeUnits ? scale : Math.max(scale, unit ? 3 : 0));
  return unit ? `${text} ${STOCK_UNIT_SUFFIX[unit]}` : text;
}

/** İşaretli miktar (hareket defteri): giriş "+", çıkış "−" (eksi işareti tipografik; ekran okuyucu "eksi" okur). */
export function formatSignedQuantity(value: string, unit?: ProductUnit | null): string {
  const decimal = Decimal.from(value);
  const text = formatStockQuantity(decimal.abs().toString(), unit);
  if (decimal.isZero()) return text;
  return `${decimal.isNegative() ? "−" : "+"}${text}`;
}

/** Excel: tutarlar kuruştan TÜRKİYE biçiminde, simgesiz (`lib/excel.ts` deseni). */
export function moneyExport(minor: string | null | undefined): string | null {
  return minor === null || minor === undefined ? null : Money.fromMinor(minor).formatTr({ symbol: false });
}

/** Excel: miktar Türkiye biçiminde, birimsiz. */
export function quantityExport(value: string | null | undefined): string | null {
  return value === null || value === undefined ? null : formatStockQuantity(value);
}

/** Yalnızca stok izlenen türlerin (market, madeni yağ, kafe) market alanları ve stok kartı vardır. */
export function isStockTracked(type: ProductType | string): boolean {
  return isStockTrackedProductType(type);
}

// ── Durum tonları ────────────────────────────────────────────────────────────
export const TRANSFER_STATUS_TONE: Record<StockTransferStatus, Tone> = {
  DRAFT: "neutral",
  SENT: "info",
  RECEIVED: "success",
  CANCELLED: "neutral",
};

export const WRITE_OFF_STATUS_TONE: Record<StockWriteOffStatus, Tone> = {
  DRAFT: "neutral",
  PENDING_APPROVAL: "warning",
  POSTED: "success",
  REJECTED: "danger",
  CANCELLED: "neutral",
};

export const COUNT_STATUS_TONE: Record<StockCountStatus, Tone> = {
  COUNTING: "info",
  SUBMITTED: "warning",
  APPROVED: "success",
  CANCELLED: "neutral",
};

export const EXPIRY_STATE_TONE: Record<ExpiryStateValue, Tone> = {
  EXPIRED: "danger",
  EXPIRING: "warning",
  OK: "success",
};

export const LEDGER_STATUS_TONE: Record<MarketLedgerStatus, Tone> = {
  NONE: "neutral",
  POSTED: "success",
  QUEUED: "warning",
};

// ── Belge eylemleri (durum makinesi — sunucu yine de denetler) ───────────────
export function transferActions(status: StockTransferStatus): {
  edit: boolean;
  send: boolean;
  receive: boolean;
  cancel: boolean;
} {
  return {
    edit: status === "DRAFT",
    send: status === "DRAFT",
    receive: status === "SENT",
    cancel: status === "DRAFT",
  };
}

export function writeOffActions(status: StockWriteOffStatus): {
  edit: boolean;
  submit: boolean;
  reopen: boolean;
  cancel: boolean;
} {
  return {
    edit: status === "DRAFT",
    submit: status === "DRAFT",
    reopen: status === "REJECTED",
    cancel: status === "DRAFT" || status === "PENDING_APPROVAL" || status === "REJECTED",
  };
}

// ── Hareketin kaynak belgesi ─────────────────────────────────────────────────
export type MovementSource =
  | { kind: "transfer"; id: string }
  | { kind: "writeOff"; id: string }
  | { kind: "count"; id: string }
  | { kind: "goodsReceipt"; id: string }
  | { kind: "invoiceMatch"; id: string }
  | { kind: "posSale"; id: string };

/**
 * Stok hareketinin (ya da partinin) kaynak belgesi → web ekranı. Kasa satışı ve iadesi kasa fişine, transfer /
 * fire / sayım / mal kabul / fatura eşleşmesi (maliyet düzeltmesi) kendi belgesine gider. Ekranı olmayan tür
 * (açılış stoğu) bağlantısız kalır — kırık bağlantı üretilmez.
 */
export function movementSource(sourceDocType: string, sourceDocId: string): MovementSource | null {
  switch (sourceDocType) {
    case "STOCK_TRANSFER":
    case "TRANSFER":
      return { kind: "transfer", id: sourceDocId };
    case "STOCK_WRITE_OFF":
      return { kind: "writeOff", id: sourceDocId };
    case "STOCK_COUNT":
    case "COUNT":
      return { kind: "count", id: sourceDocId };
    case "GOODS_RECEIPT":
      return { kind: "goodsReceipt", id: sourceDocId };
    case "INVOICE_MATCH":
      return { kind: "invoiceMatch", id: sourceDocId };
    case "POS_SALE":
    case "POS_RETURN":
    case "SALE_RETURN":
      return { kind: "posSale", id: sourceDocId };
    default:
      return null;
  }
}

export const MOVEMENT_SOURCE_KEYS = [
  "OPENING_STOCK",
  "STOCK_TRANSFER",
  "STOCK_WRITE_OFF",
  "STOCK_COUNT",
  "POS_SALE",
  "POS_RETURN",
  "GOODS_RECEIPT",
  "INVOICE_MATCH",
  "OTHER",
] as const;
export type MovementSourceKey = (typeof MOVEMENT_SOURCE_KEYS)[number];

/** Partinin kaynak türleri hareketinkinden kısadır (`TRANSFER`, `COUNT`, `OPENING`, `SALE_RETURN`). */
const LOT_SOURCE_ALIASES: Record<string, MovementSourceKey> = {
  TRANSFER: "STOCK_TRANSFER",
  COUNT: "STOCK_COUNT",
  OPENING: "OPENING_STOCK",
  SALE_RETURN: "POS_RETURN",
};

/**
 * Kaynak belge türü → çeviri anahtarı. Bilinmeyen tür (yeni sunucu, eski istemci) "Diğer belge" olarak yazılır;
 * ekran kırılmaz ve bağlantısız kaynak "Belgeyi aç" gibi yanıltıcı bir metin taşımaz.
 */
export function movementSourceKey(sourceDocType: string): MovementSourceKey {
  if ((MOVEMENT_SOURCE_KEYS as readonly string[]).includes(sourceDocType))
    return sourceDocType as MovementSourceKey;
  return LOT_SOURCE_ALIASES[sourceDocType] ?? "OTHER";
}

// ── Belge satırları (transfer, fire, açılış stoğu) ───────────────────────────
export interface DocumentLineDraft {
  /** Yalnızca listede anahtar olarak kullanılır (sunucuya gitmez). */
  key: string;
  productId: string | null;
  productLabel: string;
  /** Türkiye biçiminde metin ("12,5"). */
  quantity: string;
  /** Boş = FEFO (ilk biten ilk çıkar). */
  lotId: string | null;
  note: string;
}

let lineSequence = 0;
export function emptyDocumentLine(): DocumentLineDraft {
  lineSequence += 1;
  return {
    key: `line-${lineSequence}`,
    productId: null,
    productLabel: "",
    quantity: "",
    lotId: null,
    note: "",
  };
}

export type LineProblem = "product" | "quantity" | "duplicate";

/**
 * Satır denetimi (gönderim öncesi, istemcide): ürün seçilmeli, miktar 3 ondalığa kadar POZİTİF olmalı. Aynı ürün
 * aynı partiden iki kez girilemez (sunucu da reddeder). Tamamen boş satır SAYILMAZ (yeni satır açılıp bırakılmış).
 */
export function documentLineProblems(lines: readonly DocumentLineDraft[]): Map<string, LineProblem> {
  const problems = new Map<string, LineProblem>();
  const seen = new Set<string>();
  for (const line of lines) {
    if (isBlankLine(line)) continue;
    if (!line.productId) {
      problems.set(line.key, "product");
      continue;
    }
    const quantity = trTextToApiDecimal(line.quantity);
    if (!isPositiveQuantity(quantity)) {
      problems.set(line.key, "quantity");
      continue;
    }
    const identity = `${line.productId}:${line.lotId ?? "fefo"}`;
    if (seen.has(identity)) problems.set(line.key, "duplicate");
    seen.add(identity);
  }
  return problems;
}

export function isBlankLine(line: DocumentLineDraft): boolean {
  return !line.productId && line.quantity.trim() === "";
}

export function isPositiveQuantity(apiDecimal: string): boolean {
  if (!/^\d{1,15}(\.\d{1,3})?$/.test(apiDecimal)) return false;
  return Decimal.from(apiDecimal).isPositive();
}

/** Transfer satırları → gövde: boş satır atlanır, miktar TR → API. Not alanı transferde YOKTUR (gövdeye çıkmaz). */
export function transferLinesBody(
  lines: readonly DocumentLineDraft[],
): Array<{ productId: string; quantity: string; lotId: string | null }> {
  return lines
    .filter((line) => !isBlankLine(line))
    .map((line) => ({
      productId: line.productId ?? "",
      quantity: trTextToApiDecimal(line.quantity),
      lotId: line.lotId,
    }));
}

/** Fire satırları → gövde: satır notu boşsa `null`. */
export function writeOffLinesBody(
  lines: readonly DocumentLineDraft[],
): Array<{ productId: string; quantity: string; lotId: string | null; note: string | null }> {
  return lines
    .filter((line) => !isBlankLine(line))
    .map((line) => ({
      productId: line.productId ?? "",
      quantity: trTextToApiDecimal(line.quantity),
      lotId: line.lotId,
      note: line.note.trim() === "" ? null : line.note.trim(),
    }));
}

/** Kayıtlı belge satırı → düzenleme taslağı (miktar Türkiye biçimine döner). */
export function documentLineFrom(line: {
  productId: string;
  productCode: string;
  productName: string;
  lotId: string | null;
  quantity?: string;
  sentQuantity?: string;
  note?: string | null;
}): DocumentLineDraft {
  lineSequence += 1;
  return {
    key: `line-${lineSequence}`,
    productId: line.productId,
    productLabel: `${line.productCode} ${line.productName}`,
    quantity: apiDecimalToTrText(line.quantity ?? line.sentQuantity ?? ""),
    lotId: line.lotId,
    note: line.note ?? "",
  };
}

// ── Transfer kabulü ──────────────────────────────────────────────────────────
/**
 * Alınan miktar (TR metin) → eksik miktar. Boş metin TAM geldi demektir (sunucu da verilmeyen satırı tam sayar).
 * Geçersiz metin, negatif ya da gönderilenden FAZLA miktar `null` döner (form o satırda hata gösterir).
 */
export function receiveShortage(sentQuantity: string, receivedText: string): Decimal | null {
  const trimmed = receivedText.trim();
  if (trimmed === "") return Decimal.from(0);
  const api = trTextToApiDecimal(trimmed);
  if (!/^\d{1,15}(\.\d{1,3})?$/.test(api)) return null;
  const received = Decimal.from(api);
  const sent = Decimal.from(sentQuantity);
  if (received.gt(sent)) return null;
  return sent.sub(received);
}

export function receiveLinesBody(
  lines: ReadonlyArray<{ id: string; sentQuantity: string }>,
  received: Readonly<Record<string, string>>,
  notes: Readonly<Record<string, string>>,
): Array<{ lineId: string; receivedQuantity: string; shortNote: string | null }> {
  return lines.map((line) => {
    const text = (received[line.id] ?? "").trim();
    const note = (notes[line.id] ?? "").trim();
    return {
      lineId: line.id,
      receivedQuantity: text === "" ? Decimal.from(line.sentQuantity).toFixed(3) : trTextToApiDecimal(text),
      shortNote: note === "" ? null : note,
    };
  });
}

/**
 * Değer tutarlılığı (ADR 0016 §11): Σ gönderilen = Σ alınan + Σ eksik. Sunucunun yazdığı tutarlardan hesaplanır;
 * eşitsizlik (olmamalı) ekranda uyarı olarak gösterilir, sessizce yutulmaz.
 */
export function transferValueBalance(
  transfer: Pick<StockTransfer, "sentValue" | "receivedValue" | "shortValue">,
): {
  sent: string;
  received: string;
  short: string;
  balanced: boolean;
} {
  const sent = Money.fromMinor(transfer.sentValue);
  const received = Money.fromMinor(transfer.receivedValue);
  const short = Money.fromMinor(transfer.shortValue);
  return {
    sent: sent.toString(),
    received: received.toString(),
    short: short.toString(),
    balanced: received.add(short).eq(sent),
  };
}

// ── Açılış stoğu ─────────────────────────────────────────────────────────────
export interface OpeningStockFormValues {
  warehouseId: string;
  productId: string | null;
  /** TR metin. */
  quantity: string;
  /** TR metin, ₺, en çok 6 ondalık (KDV hariç). */
  unitCost: string;
  lotNo: string;
  /** YYYY-AA-GG ya da null. */
  expiryDate: string | null;
}

/**
 * Tek satırlık açılış stoğu gövdesi. Parti no / SKT boşsa `null` (SKT'siz üründe sunucu partiyi reddeder:
 * ekranda da yalnızca SKT takipli üründe görünür, görünmüyorsa gövdeye `null` çıkar).
 */
export function openingStockBody(values: OpeningStockFormValues, trackExpiry: boolean): unknown {
  const lotNo = values.lotNo.trim();
  return {
    warehouseId: values.warehouseId,
    lines: [
      {
        productId: values.productId ?? "",
        quantity: trTextToApiDecimal(values.quantity),
        unitCost: trTextToApiDecimal(values.unitCost),
        lotNo: trackExpiry && lotNo !== "" ? lotNo : null,
        expiryDate: trackExpiry ? values.expiryDate : null,
      },
    ],
  };
}

// ── İstasyon ürün ayarı ve ürün – tedarikçi ─────────────────────────────────
export interface StationSettingFormValues {
  minStock: string;
  maxStock: string;
  shelfLocation: string;
  isListed: boolean;
}

export function stationSettingBody(
  stationId: string,
  productId: string,
  values: StationSettingFormValues,
): unknown {
  const blankToNull = (text: string) => (text.trim() === "" ? null : trTextToApiDecimal(text));
  const shelf = values.shelfLocation.trim();
  return {
    stationId,
    items: [
      {
        productId,
        minStock: blankToNull(values.minStock),
        maxStock: blankToNull(values.maxStock),
        shelfLocation: shelf === "" ? null : shelf,
        isListed: values.isListed,
      },
    ],
  };
}

export interface ProductSupplierFormValues {
  supplierProductCode: string;
  /** TR metin, ₺ (KDV hariç, en çok 4 ondalık). */
  purchasePrice: string;
  packSize: string;
  leadTimeDays: string;
  minOrderQuantity: string;
  isPreferred: boolean;
}

export function productSupplierFields(values: ProductSupplierFormValues): Record<string, unknown> {
  const blankToNull = (text: string) => (text.trim() === "" ? null : trTextToApiDecimal(text));
  const code = values.supplierProductCode.trim();
  return {
    supplierProductCode: code === "" ? null : code,
    purchasePrice: blankToNull(values.purchasePrice),
    packSize: blankToNull(values.packSize),
    leadTimeDays: trTextToInt(values.leadTimeDays),
    minOrderQuantity: blankToNull(values.minOrderQuantity),
    isPreferred: values.isPreferred,
  };
}

// ── Market ayarları formu ────────────────────────────────────────────────────
export interface MarketSettingsFormValues {
  companyId: string;
  /** null → şirket satırı; dolu → o istasyonun geçersiz kılması. */
  stationId: string | null;
  version: number;
  matchQuantityTolerancePercent: string;
  matchPriceTolerancePercent: string;
  /** Kuruş (MoneyInput). */
  matchAmountTolerance: string | null;
  expiryWarningDays: string;
  deadStockDays: string;
  salesVelocityDays: string;
  defaultTargetMarginPercent: string;
  priceRoundTo: string;
  allowNegativeStock: boolean;
  writeOffLossKeys: Record<WriteOffReason, WriteOffLossKey>;
  writeOffVatReversal: boolean;
  /** Kuruş (MoneyInput). */
  countApprovalThreshold: string | null;
  cycleCountStaleDays: string;
  licenseWarningDays: string;
  needsVerification: boolean;
}

/**
 * API yanıtı → form durumu. İSTASYON kapsamında henüz geçersiz kılma yoksa ekran ŞİRKET satırının değerleriyle
 * dolar; hedef satırın sürümü 0'dır (kayıt yok) ve çağıran `overrides` ile verir (kasa / akaryakıt kalıbı).
 */
export function marketSettingsFormFrom(
  settings: MarketSettings,
  overrides: { stationId?: string | null; version?: number } = {},
): MarketSettingsFormValues {
  return {
    companyId: settings.companyId,
    stationId: overrides.stationId === undefined ? settings.stationId : overrides.stationId,
    version: overrides.version === undefined ? settings.version : overrides.version,
    matchQuantityTolerancePercent: apiDecimalToTrText(settings.matchQuantityTolerancePercent),
    matchPriceTolerancePercent: apiDecimalToTrText(settings.matchPriceTolerancePercent),
    matchAmountTolerance: settings.matchAmountTolerance,
    expiryWarningDays: String(settings.expiryWarningDays),
    deadStockDays: String(settings.deadStockDays),
    salesVelocityDays: String(settings.salesVelocityDays),
    defaultTargetMarginPercent: apiDecimalToTrText(settings.defaultTargetMarginPercent),
    priceRoundTo: apiDecimalToTrText(settings.priceRoundTo),
    allowNegativeStock: settings.allowNegativeStock,
    writeOffLossKeys: { ...settings.writeOffLossKeys },
    writeOffVatReversal: settings.writeOffVatReversal,
    countApprovalThreshold: settings.countApprovalThreshold,
    cycleCountStaleDays: String(settings.cycleCountStaleDays),
    licenseWarningDays: String(settings.licenseWarningDays),
    needsVerification: settings.needsVerification,
  };
}

/** Form → sözleşme biçimi (doğrulamadan ÖNCE): ondalık TR → API, gün alanları tam sayı, boş tutar 0. */
export function normalizeMarketSettingsValues(values: unknown): unknown {
  if (typeof values !== "object" || values === null) return values;
  const form = values as MarketSettingsFormValues;
  return {
    companyId: form.companyId,
    stationId: form.stationId ?? null,
    version: form.version,
    matchQuantityTolerancePercent: trTextToApiDecimal(form.matchQuantityTolerancePercent),
    matchPriceTolerancePercent: trTextToApiDecimal(form.matchPriceTolerancePercent),
    matchAmountTolerance: form.matchAmountTolerance ?? "0",
    expiryWarningDays: trTextToInt(form.expiryWarningDays),
    deadStockDays: trTextToInt(form.deadStockDays),
    salesVelocityDays: trTextToInt(form.salesVelocityDays),
    defaultTargetMarginPercent: trTextToApiDecimal(form.defaultTargetMarginPercent),
    priceRoundTo: trTextToApiDecimal(form.priceRoundTo),
    allowNegativeStock: form.allowNegativeStock,
    writeOffLossKeys: form.writeOffLossKeys,
    writeOffVatReversal: form.writeOffVatReversal,
    countApprovalThreshold: form.countApprovalThreshold ?? "0",
    cycleCountStaleDays: trTextToInt(form.cycleCountStaleDays),
    licenseWarningDays: trTextToInt(form.licenseWarningDays),
    needsVerification: form.needsVerification,
  };
}

export const marketSettingsFormSchema = z.preprocess(
  normalizeMarketSettingsValues,
  saveMarketSettingsSchema,
) as unknown as z.ZodType<z.output<typeof saveMarketSettingsSchema>, MarketSettingsFormValues>;

// ── Kategori hedef marjı ─────────────────────────────────────────────────────
/** "25,5" → "25.5"; boş → null. Geçersiz metin işaretle döner, sunucu alan hatası verir. */
export function targetMarginBody(text: string): string | null {
  return text.trim() === "" ? null : trTextToApiDecimal(text);
}
